import { toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import { useCharterBundleFallbackPricing } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useCharterBundleFallbackPricing'
import { useMaldivesCharterFareClassBundleData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useMaldivesCharterFareClassBundleData'
import {
  getCalendarBundleDisplayPrice,
  getCalendarBundleHotelPrice,
  getFallbackBundleDisplayPrice,
  getFallbackRewardHotelPrice,
} from 'components/MaldivesDirect/MaldivesDirectSearchPage/utils/bundlePricingDisplay'
import { MALDIVES_BUNDLE_DEFAULT_NIGHTS } from 'constants/charterFlight'
import { FareClassModalBundlePrice } from './MaldivesCharterFareClassModalPrice'

interface Props {
  offer?: App.HotelOffer
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
  fareClass: FareClassWithReward
  nights?: number
  luxPlusEnabled?: boolean
  /** When true, skip reward availability request and show hardcoded preview pricing instead. */
  rewardPreviewOnly?: boolean
  feederOriginAirportCode?: string
}

/**
 * Renders the "from" pricing line for a fare class card in the Maldives charter modal.
 *
 * Cash variants show `From <amount> /adult` (single horizontal row).
 * Reward variants show `From <cash> + <points> /person` with points slightly quieter.
 */
function MaldivesCharterFareClassModalBundleFromPrice({
  offer,
  checkInDate,
  checkOutDate,
  occupancies,
  fareClass,
  nights = MALDIVES_BUNDLE_DEFAULT_NIGHTS,
  luxPlusEnabled = false,
  rewardPreviewOnly = false,
  feederOriginAirportCode,
}: Props) {
  if (offer) {
    return <OfferFareClassModalBundleFromPrice
      offer={offer}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      occupancies={occupancies}
      fareClass={fareClass}
      nights={nights}
      luxPlusEnabled={luxPlusEnabled}
      rewardPreviewOnly={rewardPreviewOnly}
      feederOriginAirportCode={feederOriginAirportCode}
    />
  }

  return <SearchFareClassModalBundleFromPrice
    checkInDate={checkInDate}
    checkOutDate={checkOutDate}
    occupancies={occupancies}
    fareClass={fareClass}
    nights={nights}
    luxPlusEnabled={luxPlusEnabled}
    rewardPreviewOnly={rewardPreviewOnly}
    feederOriginAirportCode={feederOriginAirportCode}
  />
}

function OfferFareClassModalBundleFromPrice({
  offer,
  checkInDate,
  checkOutDate,
  occupancies,
  fareClass,
  nights = MALDIVES_BUNDLE_DEFAULT_NIGHTS,
  luxPlusEnabled = false,
  rewardPreviewOnly = false,
  feederOriginAirportCode,
}: Props & { offer: App.HotelOffer }) {
  const {
    fallbackPricing,
    fallbackMemberPricing,
    rewardHotelPricing,
  } = useCharterBundleFallbackPricing({
    offer,
    rooms: occupancies,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    fareClass: toBaseFareClass(fareClass),
    nights,
    displayMemberPrice: luxPlusEnabled,
    feederOriginAirportCode,
  })
  const bundlePrice = getFallbackBundleDisplayPrice({
    fallbackPricing,
    fallbackMemberPricing,
    useMemberPrice: luxPlusEnabled,
  })
  const rewardHotelPrice = getFallbackRewardHotelPrice({
    rewardHotelPricing,
    useMemberPrice: luxPlusEnabled,
  })

  return <FareClassModalBundlePrice
    checkInDate={checkInDate}
    checkOutDate={checkOutDate}
    occupancies={occupancies}
    fareClass={fareClass}
    rewardPreviewOnly={rewardPreviewOnly}
    feederOriginAirportCode={feederOriginAirportCode}
    fetching={false}
    bundlePrice={bundlePrice}
    rewardHotelPrice={rewardHotelPrice}
  />
}

function SearchFareClassModalBundleFromPrice({
  checkInDate,
  checkOutDate,
  occupancies,
  fareClass,
  nights = MALDIVES_BUNDLE_DEFAULT_NIGHTS,
  luxPlusEnabled = false,
  rewardPreviewOnly = false,
  feederOriginAirportCode,
}: Props) {
  const baseFareClass = toBaseFareClass(fareClass)
  const { bundleData, isLoading } = useMaldivesCharterFareClassBundleData({
    checkInDate,
    occupancies,
    fareClass: baseFareClass,
    nights,
    luxPlusEnabled,
    feederOriginAirportCode,
  })
  const bundlePrice = bundleData && getCalendarBundleDisplayPrice(bundleData, {
    useMemberPrice: luxPlusEnabled,
    priceMode: 'total',
  })

  return <FareClassModalBundlePrice
    checkInDate={checkInDate}
    checkOutDate={checkOutDate}
    occupancies={occupancies}
    fareClass={fareClass}
    rewardPreviewOnly={rewardPreviewOnly}
    feederOriginAirportCode={feederOriginAirportCode}
    fetching={isLoading || !!bundleData?.fetching}
    bundlePrice={bundlePrice || undefined}
    rewardHotelPrice={getCalendarBundleHotelPrice(bundleData, luxPlusEnabled)}
  />
}

export default MaldivesCharterFareClassModalBundleFromPrice
